import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, of, switchMap, tap } from 'rxjs';
import { Country } from '../models/country.model';
import { CountryService } from '../services/country.service';
import { LoadingService } from 'src/app/core/ui/loading.service';

@Injectable({ providedIn: 'root' })
export class CountryStore {
  private loadingService = inject(LoadingService);

  private static readonly PINNED_ISO_CODES = new Set(['AT', 'DE', 'CH', 'IT', 'LU']);
  private static readonly SEPARATOR: Country = { id: -1, code: '', isoCode2: '', texts: [], displayText: '', isSeparator: true };

  // Subjects für den Zustand
  private countriesSubject = new BehaviorSubject<Country[]>([]);

  // Öffentliche Observables
  countries$: Observable<Country[]> = this.countriesSubject.asObservable();

  countriesWithSeparator$: Observable<Country[]> = this.countriesSubject.pipe(
    map(countries => {
      const lastPinnedIdx = countries.reduce(
        (last, c, i) => CountryStore.PINNED_ISO_CODES.has(c.isoCode2) ? i : last, -1
      );
      if (lastPinnedIdx < 0 || lastPinnedIdx >= countries.length - 1) return countries;
      const result = [...countries];
      result.splice(lastPinnedIdx + 1, 0, CountryStore.SEPARATOR);
      return result;
    })
  );

  constructor(private countryService: CountryService) {}

  /**
   * Lädt alle Länder und aktualisiert den Zustand.
   */
loadCountries(): Observable<Country[]> {
  this.loadingService.show();

  return this.countryService.fetchCountries().pipe(
    switchMap((countries) => {
      countries.forEach((country) => {
        country.displayText = country.texts[0]?.description ?? country.code;
      });
      this.countriesSubject.next(countries);
      return of(countries);
    }),
    finalize(() => {
      this.loadingService.hide();
    })
  );
}

  /**
   * Lädt ein einzelnes Land anhand der ID.
   */
  loadCountryById(id: number): Observable<Country> {
    this.loadingService.show()

    return new Observable<Country>((observer) => {
      this.countryService.getCountryById(id).subscribe({
        next: (country) => {
          country.displayText = country.texts[0]?.description ?? country.code;
          observer.next(country);
          observer.complete();
        },
        complete: () => {
          this.loadingService.hide()
        },
      });
    });
  }

  /**
   * Gibt die aktuelle Liste der Länder zurück.
   */
  getCountriesSnapshot(): Country[] {
    return this.countriesSubject.getValue();
  }
}